import { useState, useEffect, useCallback, useRef } from 'react';
import { IApiData } from '../shared/types';
import { ReconnectingWebSocket } from '../socket'; // Updated import
import { deriveSwapProgressFromBalancingSteps } from '../utils/botUtils';

/** How long a good balance may stand in for an all-zero snapshot (a transient read). */
const BALANCE_CACHE_MAX_AGE_MS = 10 * 60 * 1000;

// Cache structure to store last known balances for each bot
interface BalanceCache {
    [botId: string]: {
        walletValueUSD: number;
        poolValueUSD: number;
        totalValueInUSD: number;
        timestamp: number;
    };
}

export function useApiData(socket: ReconnectingWebSocket) { // Accept socket as prop
    const [data, setData] = useState<IApiData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isDataEmpty, setIsDataEmpty] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // Cache to store last known balances for each bot
    const balanceCacheRef = useRef<BalanceCache>({});

    const handleData = useCallback((apiData: IApiData) => {
        // Create a deep copy to avoid mutating the original data
        const processedData = { ...apiData };
        
        if (processedData.botsData) {
            processedData.botsData = { ...processedData.botsData };
            
            // Process each bot's data
            Object.keys(processedData.botsData).forEach(botId => {
                const bot = processedData.botsData[botId];
                if (!bot || !bot.stats) return;

                let nextBot = bot;
                const derivedSwapProgress = deriveSwapProgressFromBalancingSteps(bot.stats, bot.balancingSteps);
                if (Object.keys(derivedSwapProgress).length > 0) {
                    nextBot = {
                        ...nextBot,
                        stats: {
                            ...nextBot.stats,
                            ...derivedSwapProgress,
                        },
                    };
                }

                if (!nextBot.stats.balanceSnapshotEnd) {
                    processedData.botsData[botId] = nextBot;
                    return;
                }
                
                const currentBalance = nextBot.stats.balanceSnapshotEnd;
                const cachedBalance = balanceCacheRef.current[botId];
                
                // A pool (or wallet) at $0 is a real state: the bot zeroes the pool when a position closes
                // and while it balances. Only a snapshot that is empty everywhere is treated as a transient
                // glitch, and only shortly after a good one; otherwise the payload is shown as it is.
                const isEmptySnapshot =
                    !(currentBalance.walletValueUSD > 0) &&
                    !(currentBalance.poolValueUSD > 0) &&
                    !(currentBalance.totalValueInUSD > 0);
                const cacheIsFresh = !!cachedBalance && Date.now() - cachedBalance.timestamp < BALANCE_CACHE_MAX_AGE_MS;
                const hasZeroBalance = isEmptySnapshot;

                // Transient empty snapshot right after a valid one: keep the last values.
                if (isEmptySnapshot && cacheIsFresh) {
                    // Create a copy of the bot with preserved balance
                    nextBot = {
                        ...nextBot,
                        stats: {
                            ...nextBot.stats,
                            balanceSnapshotEnd: {
                                ...nextBot.stats.balanceSnapshotEnd,
                                walletValueUSD: cachedBalance.walletValueUSD,
                                poolValueUSD: cachedBalance.poolValueUSD,
                                totalValueInUSD: cachedBalance.totalValueInUSD,
                            }
                        }
                    };
                } 
                // Any non-empty snapshot is the truth: remember it.
                else if (!hasZeroBalance) {
                    balanceCacheRef.current[botId] = {
                        walletValueUSD: currentBalance.walletValueUSD,
                        poolValueUSD: currentBalance.poolValueUSD,
                        totalValueInUSD: currentBalance.totalValueInUSD,
                        timestamp: Date.now(),
                    };
                }

                processedData.botsData[botId] = nextBot;
            });
        }
        
        setData(processedData);
        const isEmpty = Object.keys(processedData.botsData || {}).length === 0;
        setIsDataEmpty(isEmpty);
        if (isInitialLoad) {
            setLoading(false);
            setIsInitialLoad(false);
        }
    }, [isInitialLoad]);

    useEffect(() => {
        if (!socket) return; // si aún no existe, no hace nada

        const handleError = (error: any) => {
            setError(`Failed to load data: ${error.message}`);
            console.error('API Error:', error);
            setLoading(false);
        };

        socket.on('initialData', handleData);
        socket.on('dataUpdated', handleData);
        socket.on('dataError', handleError);

        return () => {
            socket.off('initialData', handleData);
            socket.off('dataUpdated', handleData);
            socket.off('dataError', handleError);
        };
    }, [handleData, socket]);

    const refreshData = useCallback(() => {
        // If you need to explicitly request a refresh, you can send a WebSocket message here
        // For now, assuming backend pushes updates
    }, []);

    return { data, loading, error, refreshData, isDataEmpty };
}
